import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAIL = "swarajrp030@gmail.com";

const PendingReview = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (me.data?.email !== ADMIN_EMAIL) {
          toast.error("Admins only");
          navigate("/");
          return;
        }
        setUser(me.data);
      } catch {
        toast.error("Please sign in");
        navigate("/login");
        return;
      } finally {
        setAuthChecking(false);
      }

      try {
        const res = await axios.get(`${API}/pending-submissions`, { withCredentials: true });
        setSubmissions(res.data || []);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load pending submissions");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const handleDiscard = async (id) => {
    if (!window.confirm("Discard this submission permanently?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/pending-submissions/${id}`, { withCredentials: true });
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Submission discarded");
    } catch (e) {
      console.error(e);
      toast.error("Failed to discard");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="pending-loading">
        <p className="text-[#6B6A68]">Checking access…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="pending-review-page">
      <header
        className="sticky top-0 z-40 bg-[#F9F8F6]/90 backdrop-blur-xl border-b border-[#E4E1D9] py-4 px-6 md:px-12 flex justify-between items-center"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="p-2 hover:bg-[#F0EFEB] rounded-md transition-colors"
            data-testid="back-button"
            aria-label="Back to discovery"
          >
            <ArrowLeft size={20} weight="bold" className="text-[#2D302A]" />
          </button>
          <h1
            className="text-2xl md:text-3xl tracking-tighter"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Pending Submissions
          </h1>
        </div>
        <span
          className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="pending-count"
        >
          {submissions.length} {submissions.length === 1 ? "item" : "items"}
        </span>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <p className="text-center py-20 text-[#6B6A68]" data-testid="pending-loading-list">Loading…</p>
        ) : submissions.length === 0 ? (
          <div className="text-center py-24" data-testid="pending-empty">
            <h2
              className="text-3xl md:text-4xl mb-3 tracking-tighter"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              All clear
            </h2>
            <p className="text-[#6B6A68]">No pending submissions right now.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="pending-list">
            <p className="text-sm text-[#6B6A68] mb-2">
              Review each link, then create the live product yourself using the <span className="text-[#2D302A]">Share a Fit</span> button on the discovery page when it's worth featuring.
            </p>
            {submissions.map((s, idx) => (
              <article
                key={s.id}
                className="bg-white border border-[#E4E1D9] rounded-md p-4 md:p-5 flex flex-col md:flex-row gap-5"
                data-testid={`pending-item-${idx}`}
              >
                {s.image_url ? (
                  <img
                    src={`${BACKEND_URL}${s.image_url}`}
                    alt={s.product_name || "Pending submission"}
                    className="w-full md:w-40 h-40 object-cover rounded border border-[#E4E1D9]"
                    data-testid={`pending-image-${idx}`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full md:w-40 h-40 bg-[#F0EFEB] border border-[#E4E1D9] rounded flex items-center justify-center text-xs text-[#6B6A68]">
                    No image
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3
                    className="text-xl md:text-2xl tracking-tight mb-2 line-clamp-2"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                    data-testid={`pending-name-${idx}`}
                  >
                    {s.product_name || "Untitled"}
                  </h3>

                  <a
                    href={s.product_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#2D302A] hover:underline break-all"
                    data-testid={`pending-link-${idx}`}
                  >
                    {s.product_link.length > 80 ? s.product_link.slice(0, 80) + "…" : s.product_link}
                    <ArrowSquareOut size={14} weight="bold" />
                  </a>

                  <div className="text-xs text-[#6B6A68] mt-2" data-testid={`pending-meta-${idx}`}>
                    Submitted by {s.user_name || "Unknown"}
                    {s.user_email ? ` · ${s.user_email}` : ""} ·{" "}
                    {new Date(s.created_at).toLocaleString()}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      onClick={() => handleCopyLink(s.product_link)}
                      className="px-3 py-2 text-xs uppercase tracking-[0.2em] bg-white border border-[#E4E1D9] hover:border-[#2D302A] rounded transition-colors"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      data-testid={`pending-copy-${idx}`}
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => handleDiscard(s.id)}
                      disabled={deletingId === s.id}
                      className="px-3 py-2 text-xs uppercase tracking-[0.2em] bg-white border border-[#E4E1D9] hover:border-red-500 hover:text-red-600 rounded transition-colors flex items-center gap-1.5 disabled:opacity-50"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      data-testid={`pending-discard-${idx}`}
                    >
                      <Trash size={14} weight="bold" />
                      {deletingId === s.id ? "Discarding…" : "Discard"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PendingReview;
